import axios from "axios";
import { configService } from "@/infrastructure/services/ConfigService";
import { logger } from "./LoggerService";
export class ApiPublicPaymentService {
    constructor() {
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        const apiConfig = configService.api;
        this.baseUrl = apiConfig?.baseUrl || "http://localhost:8080/api/v1";
    }
    async getOrganizationInfo(paymentId) {
        try {
            // GET opay.eudonet.com/api/v1/public/infos/{paymentId}
            const response = await axios.get(`${this.baseUrl}/public/infos/${paymentId}`);
            // Expected response format from backend (with lowercase keys):
            /* {
              "success": true,
              "errorCode": "None",
              "calledApiErrorCode": "",
              "message": "",
              "result": {
                "opayAccountName": "Test Account",
                "opayAccountLogo": "https://example.com/logo.png",
                "locale": "fr-FR"
              },
              "errorResult": null
            } */
            if (!response.data.success || !response.data.result) {
                // If no data but no explicit error, might be an ErrorCode response
                throw new Error("No data received or request was unsuccessful");
            }
            // Map API response fields to domain entity format
            const result = response.data.result;
            return {
                locale: result.locale,
                opayAccountName: result.opayAccountName || result.opayaccountname,
                opayAccountLogo: result.opayAccountLogo || result.opayaccountlogo,
                legalName: result.legalName,
                beneficiary: result.beneficiary,
            };
        }
        catch (error) {
            logger.debug("Failed to fetch organization info:", error);
            // Let the error store handle ErrorCode and HTTP status codes
            // Don't override error messages here - let errorStore handle them
            throw error;
        }
    }
    async getPaymentSession(paymentId) {
        try {
            // Get the browser's timezone using IANA format (e.g., "America/Montreal", "Europe/Paris")
            const timeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
            // POST opay.eudonet.com/api/v1/public/payment-session with paymentId and timeZoneId in body
            const response = await axios.post(`${this.baseUrl}/public/payment-session`, {
                paymentId,
                timeZoneId,
            });
            // Expected response format (with lowercase keys):
            /*
              {
                "success": true,
                "errorCode": "None",
                "calledApiErrorCode": "",
                "message": "Payment session created successfully",
                "result": {
                  "sessionId": "xxxx",
                  "sessionData": "Asss"
                },
                "errorResult": null
              }
            */
            // Error: { success: false, errorCode: number/string }
            logger.debug("Payment session response:", response.data);
            // For Sessions Flow, sessionData should contain the complete session object
            if (!response.data.result?.sessionId || !response.data.result?.sessionData) {
                throw new Error("No sessionId received from backend");
            }
            return response.data.result;
        }
        catch (error) {
            logger.debug("Failed to fetch payment token:", error);
            throw error;
        }
    }
    async getPaymentDetails(paymentId) {
        try {
            // API endpoint for payment details
            const response = await axios.get(`${this.baseUrl}/public/payment/${paymentId}`);
            // Expected response format as documented in the API (with lowercase keys):
            /*
              {
                "success": true,
                "errorCode": "None",
                "calledApiErrorCode": "",
                "message": "",
                "result": {
                  "amount": 1000,
                  "reference": "string",
                  "taxAmount": 100,
                  "dueDate": null,
                  "currency": "EUR",
                  "pspReference": "string",
                  "paymentMethod": "Unknown",
                  "paymentStatus": "Paid",
                  "urlReturn": "https://string.com" // or null for default success page
                },
                "errorResult": null
              }
            */
            // Check if response has data and result
            logger.debug("Payment details response:", response.data);
            if (!response.data.success || !response.data.result) {
                throw new Error("No payment details received or request was unsuccessful");
            }
            // Return the API response result object directly without property mapping
            return response.data.result;
        }
        catch (error) {
            logger.debug("Failed to fetch payment details:", error);
            throw error;
        }
    }
    async updatePadType(paymentId, padType) {
        try {
            // PATCH /api/v1/public/payment/{paymentId}/pad-type
            await axios.patch(`${this.baseUrl}/public/payment/${paymentId}/pad-type`, { padType });
        }
        catch (error) {
            logger.debug("Failed to update PAD type:", error);
            throw error;
        }
    }
    async cancelPayment(paymentId) {
        try {
            // PUT/api/v1/public/payment/{paymentId}/cancel
            const response = await axios.put(`${this.baseUrl}/public/payment/${paymentId}/cancel`);
            logger.debug("Payment cancellation response:", response.data);
            // Check if cancellation was successful
            if (response.data && !response.data.success) {
                throw new Error("Payment cancellation was unsuccessful");
            }
        }
        catch (error) {
            logger.debug("Failed to cancel payment:", error);
            throw error;
        }
    }
}
